import React, { Component, type ReactNode } from 'react';
import createRaf from '../../../../../../utils/create-request-animation-frame.tsx';
import ScrollContext, { type Register, type Unregister, type SyncAxis } from '../context';

const requestCancellableAnimationFrame = createRaf();

type Listener = {
	node: HTMLElement;
	syncAxis?: SyncAxis;
};

type Listeners = Listener[];

type Props = {
	children: ReactNode;
};

type State = {
	register: Register;
	unregister: Unregister;
};

// eslint-disable-next-line jira/react/no-class-components
class ScrollSynchronizeProvider extends Component<Props, State> {
	constructor(props: Props) {
		super(props);

		this.listeners = [];

		// It is being used, but only as a whole state object and the linter does not recognize that
		/* eslint-disable react/no-unused-state */
		this.state = {
			register: this.onRegister,
			unregister: this.onUnregister,
		};
		/* eslint-enable react/no-unused-state */
	}

	onRegister: Register = (node, syncAxis) => {
		// Assert: element is not already registered
		if (this.findListener(node) !== undefined) {
			throw new Error(
				'An element should only be registered once. Is it being registered in a sensible component life-cycle (e.g. componentDidMount)?',
			);
		}

		this.listenOnNode(node);
		this.listeners.push({ node, syncAxis });
	};

	onUnregister: Unregister = (node) => {
		// Assert: element is already registered
		if (this.findListener(node) === undefined) {
			throw new Error('The element is not registered, it cannot be unregistered.');
		}

		this.stopListeningOnNode(node);
		// @ts-expect-error - TS2367 - This condition will always return 'true' since the types 'Listener' and 'HTMLElement' have no overlap.
		this.listeners = this.listeners.filter((listener: Listener) => listener !== node);
	};

	onScroll = (event: Event) => {
		// @ts-expect-error - TS2339 - Property 'node' does not exist on type 'Listener | undefined'. | TS2339 - Property 'syncAxis' does not exist on type 'Listener | undefined'. | TS2345 - Argument of type 'EventTarget | null' is not assignable to parameter of type 'HTMLElement'.
		const { node, syncAxis } = this.findListener(event.target);

		requestCancellableAnimationFrame(() => {
			this.synchronizeScrollPositions(node, syncAxis);
		});
	};

	synchronizeScrollPositions(scrollingNode: HTMLElement, scrollingSyncAxis?: SyncAxis) {
		const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } =
			scrollingNode;

		const scrollTopOffset = scrollHeight - clientHeight;
		const scrollLeftOffset = scrollWidth - clientWidth;

		this.listeners.forEach(({ node, syncAxis }) => {
			/* For the areas that are not scrolling:
			 * 1) Stop listening to scroll events so we don't get stuck in a scroll loop
			 * 2) Synchronize the scroll positions with the current scrolling area
			 * 3) Start listening to scroll events again
			 */
			if (node !== scrollingNode) {
				this.stopListeningOnNode(node);

				/* The synchronized areas could be different heights. The scroll offsets
				 * need to be updated relative to the current scrolling area, rather than
				 * set to it's absolute value.
				 */
				const paneHeight = node.scrollHeight - clientHeight;
				const paneWidth = node.scrollWidth - clientWidth;

				// If currently not overflowed, could get a divide by 0
				if (scrollTopOffset > 0 && syncAxis !== 'x' && scrollingSyncAxis !== 'x') {
					const newScrollTop = (paneHeight * scrollTop) / scrollTopOffset;
					// eslint-disable-next-line no-param-reassign
					node.scrollTop = newScrollTop;
				}

				if (scrollLeftOffset > 0 && syncAxis !== 'y' && scrollingSyncAxis !== 'y') {
					const newScrollLeft = (paneWidth * scrollLeft) / scrollLeftOffset;
					// eslint-disable-next-line no-param-reassign
					node.scrollLeft = newScrollLeft;
				}

				// Don't attach the listener until the next frame, AFTER the scrolling has stopped
				// We always have to restore listener, so we should never cancel this frame
				requestAnimationFrame(() => {
					this.listenOnNode(node);
				});
			}
		});
	}

	findListener(node: HTMLElement): Listener | undefined {
		return this.listeners.find((listener: Listener) => listener.node === node);
	}

	listenOnNode(node: HTMLElement) {
		node.addEventListener('scroll', this.onScroll);
	}

	stopListeningOnNode(node: HTMLElement) {
		node.removeEventListener('scroll', this.onScroll);
	}

	// FIXME:
	// componentWillUnmount() {
	// cleanup listeners
	// }

	listeners: Listeners;

	frame: number | null = null;

	render() {
		const { children } = this.props;

		return <ScrollContext.Provider value={this.state}>{children}</ScrollContext.Provider>;
	}
}

export default ScrollSynchronizeProvider;
